import * as React from 'react';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import {
  View,
  StyleSheet,
  FlatList,
  Platform,
  ListRenderItem,
} from 'react-native';
import { IUserWorkoutSessionSummary } from 'fitbeat.models';
import { Heading, HeadingTypes } from '../components';
import UserDataConsumer from '../utils/UserDataConsumer';
import Theme from '../theme';
import {
  WorkoutHistoryRow,
  WorkoutHistoryModal,
  WorkoutHistoryHeader,
} from './components';
import { toggleWorkoutHistoryModal, LoadingModal } from '../ui';
import {
  sortByDate,
  sortByDuration,
  sortByActivityType,
  sortByBodyGroups,
  sortByLoad,
  sortByCals,
  sortByAverageHeartRate,
} from './utils';
import { trackingWorkoutsHistoryTabSelected, openTrackingWorkoutHistoryModal } from '../Tracking/actions';

interface IWorkoutHistoryScreenProps {
  isModalVisible: boolean;
  user: {
    workoutSessionService: {
      getMyWorkoutHistory: () => Promise<IUserWorkoutSessionSummary[]>;
    };
    userProfileService: any;
    userProfile: {
      uid: string;
      gender: any;
    };
  };
  navigation: {
    addListener: (event: string, callback: () => void) => { remove: () => void };
  };
  toggleWorkoutHistoryModal: (visible: boolean) => void;
  trackingWorkoutsHistoryTabSelected: () => void;
  openTrackingWorkoutHistoryModal: () => void;
}

interface IWorkoutHistoryScreenState {
  history: IUserWorkoutSessionSummary[];
  field: string;
  order: 'asc' | 'desc';
  sortbyFn: (history: IUserWorkoutSessionSummary[], order: 'asc' | 'desc') => IUserWorkoutSessionSummary[];
  isLoading: boolean;
  currentWorkoutHistory: IUserWorkoutSessionSummary;
  renderHistoryRows: boolean;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Theme.GREY_LIGHT,
  },
  buttonContainer: {
    flex: 1,
  },
  rowContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  noHistoryText: {
    ...Theme.textStyle.bodyCopyReg,
    textAlign: 'center',
  },
});

class WorkoutHistoryScreen extends React.Component<IWorkoutHistoryScreenProps, IWorkoutHistoryScreenState> {
  private onFocusListener?: { remove: () => void };

  constructor(props: IWorkoutHistoryScreenProps) {
    super(props);
    this.state = {
      history: [],
      field: 'sortByDate',
      order: 'desc',
      sortbyFn: sortByDate,
      isLoading: true,
      currentWorkoutHistory: {} as IUserWorkoutSessionSummary,
      renderHistoryRows: true,
    };
  }

  public componentDidMount(): void {
    this.getWorkoutHistory().catch((error) => {
      // Error handling removed to comply with TSLint rules
    });
    const { navigation, trackingWorkoutsHistoryTabSelected: trackingAction } = this.props;
    this.onFocusListener = navigation.addListener('didFocus', () => {
      trackingAction();
    });
  }

  public componentDidUpdate(prevProps: IWorkoutHistoryScreenProps): void {
    const { user } = this.props;
    if (user.userProfile !== prevProps.user.userProfile) {
      this.getWorkoutHistory().catch((error) => {
        // Error handling removed to comply with TSLint rules
      });
    }
  }

  public componentWillUnmount(): void {
    if (typeof this.onFocusListener?.remove === 'function') {
      this.onFocusListener.remove();
    }
  }

  public render(): React.ReactNode {
    const { isModalVisible, user } = this.props;
    const {
      userProfileService,
      userProfile: {
        uid,
        gender,
      },
    } = user;
    const {
      history, isLoading, currentWorkoutHistory, renderHistoryRows,
    } = this.state;
    let modalTitle = '';
    if (currentWorkoutHistory.startTime) {
      const startToShow = currentWorkoutHistory.startTimeOfFirstExercise
        ? currentWorkoutHistory.startTimeOfFirstExercise : currentWorkoutHistory.startTime;
      modalTitle = (moment(startToShow).format('D MMMM YYYY | HH:mm')).toUpperCase();
    }

    if (isLoading) {
      return (
        <View style={styles.container}>
          <LoadingModal isLoading style={styles.container} />
        </View>
      );
    }

    return (
      <View style={styles.container}>
        {renderHistoryRows
                && (
                <FlatList<IUserWorkoutSessionSummary>
                  data={history}
                  removeClippedSubviews // bad performance on Android, but default true crashes on 0.59
                  extraData={this.state}
                  showsVerticalScrollIndicator={false}
                  keyExtractor={(item) => item.uniqueReference}
                  contentContainerStyle={{
                    overflow: 'hidden',
                  }}
                  stickyHeaderIndices={[0]}
                  ListHeaderComponent={!isLoading && history.length > 0
                        && (
                        <WorkoutHistoryHeader
                          sortByDate={this.sortByDate}
                          sortByDuration={this.sortByDuration}
                          sortByActivityType={this.sortByActivityType}
                          sortByBodyGroups={this.sortByBodyGroups}
                          sortByLoad={this.sortByLoad}
                          sortByCals={this.sortByCals}
                          sortByAverageHeartRate={this.sortByAverageHeartRate}
                          field={this.state.field}
                          order={this.state.order}
                        />
                        )}
                  ListEmptyComponent={
                        !isLoading && (
                        <Heading
                          title='You have no workout history yet. Book a workout first!'
                          style={styles.noHistoryText}
                          headingType={HeadingTypes.HEADING_TWO}
                        />
                        )
                    }
                  renderItem={this.renderItem}
                />
                )}

        {isModalVisible
                    && (
                    <WorkoutHistoryModal
                      gender={gender}
                      modalTitle={modalTitle}
                      closeModal={this.closeModal}
                      isModalVisible={isModalVisible}
                      workoutHistory={currentWorkoutHistory}
                      userProfileService={userProfileService}
                    />
                    )}
      </View>
    );
  }

  private renderItem: ListRenderItem<IUserWorkoutSessionSummary> = ({ item, index }) => {
    const { user } = this.props;
    return (
      <WorkoutHistoryRow
        index={index}
        userId={user.userProfile.uid}
        gender={user.userProfile.gender}
        workoutHistory={item}
        openWorkoutHistory={this.openWorkoutHistory}
      />
    );
  };

  private getWorkoutHistory = async (): Promise<void> => {
    const { sortbyFn, field, order } = this.state;
    const { user } = this.props;
    const { getMyWorkoutHistory } = user.workoutSessionService;

    this.setState({ isLoading: true });
    const history = await getMyWorkoutHistory();
    this.sort(sortbyFn, field, history, order);
  };

  private openWorkoutHistory = async (workoutHistory: IUserWorkoutSessionSummary): Promise<void> => {
    const { toggleWorkoutHistoryModal: toggleModal, openTrackingWorkoutHistoryModal: openModal } = this.props;
    this.setState({ currentWorkoutHistory: workoutHistory });

    if (Platform.OS !== 'web') {
      this.setState({ renderHistoryRows: false });
    }

    toggleModal(true);
    openModal();
  };

  private closeModal = async (): Promise<void> => {
    const { toggleWorkoutHistoryModal: toggleModal } = this.props;
    this.setState({ renderHistoryRows: true });
    toggleModal(false);
  };

  private getReverseOrder = (order: 'asc' | 'desc'): 'asc' | 'desc' => (order === 'desc' ? 'asc' : 'desc');

  private sort = (
    sortbyFn: (history: IUserWorkoutSessionSummary[], order: 'asc' | 'desc') => IUserWorkoutSessionSummary[],
    field: string,
    history: IUserWorkoutSessionSummary[],
    forcedOrder?: 'asc' | 'desc',
  ) => {
    this.setState({
      isLoading: true,
    });

    setTimeout(async () => {
      const { order: stateOrder, field: stateField } = this.state;
      let order = stateOrder;
      if (forcedOrder) {
        order = forcedOrder;
      } else if (stateField !== field) {
        order = 'desc';
      } else {
        order = this.getReverseOrder(order);
      }
      const sortedHistory = sortbyFn(history, order);
      this.setState({
        history: sortedHistory,
        field,
        order,
        sortbyFn,
        isLoading: false,
      });
    }, 0);
  };

  private sortByDate = async (): Promise<void> => {
    const { history } = this.state;
    this.sort(sortByDate, 'sortByDate', history);
  };

  private sortByDuration = async (): Promise<void> => {
    const { history } = this.state;
    this.sort(sortByDuration, 'sortByDuration', history);
  };

  private sortByActivityType = async (): Promise<void> => {
    const { history } = this.state;
    this.sort(sortByActivityType, 'sortByActivityType', history);
  };

  private sortByBodyGroups = async (): Promise<void> => {
    const { history } = this.state;
    this.sort(sortByBodyGroups, 'sortByBodyGroups', history);
  };

  private sortByLoad = async (): Promise<void> => {
    const { history } = this.state;
    this.sort(sortByLoad, 'sortByLoad', history);
  };

  private sortByCals = async (): Promise<void> => {
    const { history } = this.state;
    this.sort(sortByCals, 'sortByCals', history);
  };

  private sortByAverageHeartRate = async (): Promise<void> => {
    const { history } = this.state;
    this.sort(sortByAverageHeartRate, 'sortByAverageHeartRate', history);
  };
}

interface IRootState {
  ui: {
    workoutHistoryModalVisible: boolean;
  };
}

const mapStateToProps = (state: IRootState) => ({
  isModalVisible: state.ui.workoutHistoryModalVisible,
});

const mapDispatchToProps = (dispatch: (action: any) => any) => ({
  toggleWorkoutHistoryModal: (visible: boolean) => dispatch(toggleWorkoutHistoryModal(visible)),
  trackingWorkoutsHistoryTabSelected: () => dispatch(trackingWorkoutsHistoryTabSelected()),
  openTrackingWorkoutHistoryModal: () => dispatch(openTrackingWorkoutHistoryModal()),
});

const ConnectedWorkoutHistoryScreen = connect(mapStateToProps, mapDispatchToProps)(WorkoutHistoryScreen);
export default UserDataConsumer(ConnectedWorkoutHistoryScreen);
